import type { SVGProps } from 'react';

/*
 * Single-colour inline-SVG icon set. Tabler-style paths (MIT), committed here
 * rather than pulled from a CDN at runtime (per ADR / design-spec §Iconography).
 * Colour is driven by `currentColor`; size + stroke are props.
 */
const ICON_PATHS = {
  home: '<path d="M3 11l9-8 9 8"/><path d="M5 9v11h14V9"/><path d="M9 20v-6h6v6"/>',
  map: '<path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2Z"/><path d="M9 4v14M15 6v14"/>',
  book: '<path d="M5 4h11a3 3 0 0 1 3 3v13H7a2 2 0 0 0-2 2V4Z"/><path d="M5 18a2 2 0 0 1 2-2h12"/>',
  photo:
    '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-5-5L5 21"/>',
  tool: '<path d="M7 10 3 6l2-2 4 4M14 7a4 4 0 0 1 5 5l-2-1-2 2 1 2a4 4 0 0 1-5-5"/><path d="m13 13-8 8"/>',
  list: '<path d="M9 6h11M9 12h11M9 18h11"/><path d="M4.5 6h.01M4.5 12h.01M4.5 18h.01"/>',
  lock: '<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>',
  help: '<circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 0 1 4.5 1.5c0 1.5-2 2-2 3.5M12 17h.01"/>',
  chevron: '<path d="M6 9l6 6 6-6"/>',
  pencil: '<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/>',
  trash:
    '<path d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  user: '<circle cx="12" cy="8" r="4"/><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1"/>',
  logout:
    '<path d="M14 8V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2v-2"/><path d="M9 12h12l-3-3m0 6 3-3"/>',
  x: '<path d="M6 6l12 12M18 6 6 18"/>',
  back: '<path d="M19 12H5M11 6l-6 6 6 6"/>',
  check: '<path d="M5 12l5 5L20 7"/>',
  alert: '<circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16h.01"/>',
  upload: '<path d="M12 16V4M7 9l5-5 5 5"/><path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3"/>',
  /* Iter-6 POI category icons — Tabler shapes, 24×24 single-stroke. */
  swimming:
    '<circle cx="17" cy="6" r="2"/><path d="M3 12c2 0 2-1 4-1s2 1 4 1 2-1 4-1 2 1 4 1"/><path d="M3 16c2 0 2-1 4-1s2 1 4 1 2-1 4-1 2 1 4 1"/><path d="M3 20c2 0 2-1 4-1s2 1 4 1 2-1 4-1 2 1 4 1"/>',
  fish: '<path d="M16.7 10.4a4 4 0 0 0 0-.8 4 4 0 0 0-4-4c-1.4 0-2.6.7-3.4 1.8C8 8.3 5.5 11 3 12c2.5 1 5 3.7 6.3 4.6.8 1.1 2 1.8 3.4 1.8a4 4 0 0 0 4-4c0-.3 0-.5 0-.8L21 16V8z"/><circle cx="13" cy="11" r="0.5" fill="currentColor"/>',
  anchor:
    '<circle cx="12" cy="5" r="2"/><path d="M12 7v15"/><path d="M5 12h14"/><path d="M19 16a7 7 0 0 1-14 0"/>',
  binoculars:
    '<path d="M4 6h4l1 2v9a3 3 0 0 1-6 0V8a2 2 0 0 1 1-2z"/><path d="M20 6h-4l-1 2v9a3 3 0 0 0 6 0V8a2 2 0 0 0-1-2z"/><path d="M9 8h6"/><path d="M10 11h4"/>',
  'tools-kitchen-2':
    '<path d="M19 3c1 4 1 7 0 11l-2 1V21M14 4l-3 4 .5 1.5L8 13l-5-5 3.5-3.5L8 5l4-3z"/><path d="M11 13l-7 7"/>',
  'map-pin':
    '<path d="M12 21s-7-6.5-7-12a7 7 0 0 1 14 0c0 5.5-7 12-7 12z"/><circle cx="12" cy="9" r="2.5"/>',
} as const;

export type IconName = keyof typeof ICON_PATHS;

type IconProps = {
  name: IconName;
  size?: number;
  /** SVG stroke width. Named distinctly from SVG's string `stroke` (colour) attr. */
  stroke?: number;
} & Omit<SVGProps<SVGSVGElement>, 'name' | 'stroke'>;

export function Icon({ name, size = 24, stroke = 1.8, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      // Paths are a fixed internal allow-list (ICON_PATHS) — no user input flows here.
      dangerouslySetInnerHTML={{ __html: ICON_PATHS[name] }}
      {...rest}
    />
  );
}
